import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback } from "react";

/**
 * Navigate to the chat surface while preserving whichever shell the
 * user is currently inside. The chat hook is rendered in four places:
 *   - standalone `/chat`
 *   - project-scoped `/projects/$id/chat`
 *   - the project Workbench's CopilotRail at `/projects/$id/workbench`
 *   - the project Requirements' CopilotRail at
 *     `/projects/$id/requirements`
 *
 * URL writes (new session id, new-chat reset, session switch) must keep
 * the user where they are, otherwise sending a message inside the
 * Workbench / Requirements rail would yank them out to standalone
 * `/chat` mid-stream and remount the conversation tree (wiping live
 * artefacts in the process).
 *
 * `opts.projectId` overrides the URL-derived shell. Pass an explicit
 * project id to force `/projects/$id/chat`, pass `null` to force the
 * standalone `/chat`, or omit `opts` entirely to preserve the current
 * shell. The override exists so the sidebar's session list can
 * auto-promote a project-bound session into its project's shell when
 * the user is currently on standalone `/chat` (and vice versa) — same
 * behaviour the History page already implements directly via navigate.
 */
export function useNavigateChat(): (
  search: { s?: string },
  opts?: { projectId?: string | null },
) => void {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Match every project surface that hosts a live ChatConversation —
  // they all share the chatStore singleton, so URL updates during a
  // turn must stay on whichever surface the user actually started on.
  const projectShellMatch = pathname.match(
    /^\/projects\/([^/]+)\/(chat|workbench|requirements)/,
  );
  const projectIdFromUrl = projectShellMatch?.[1] ?? null;
  const shellFromUrl = (projectShellMatch?.[2] ?? null) as
    | "chat"
    | "workbench"
    | "requirements"
    | null;

  return useCallback(
    (search, opts) => {
      // `"projectId" in opts` distinguishes "explicitly passed null" from
      // "omitted" — a null override forces standalone /chat, while
      // omission falls back to whatever shell the URL already shows.
      const overridden = opts && "projectId" in opts;
      const targetProjectId = overridden ? opts.projectId : projectIdFromUrl;
      if (targetProjectId) {
        // Stay on the same project shell when that's where we are AND
        // the caller hasn't explicitly overridden the project —
        // overriding implies a deliberate jump (sidebar session
        // promotion etc.) so the canonical /chat surface is the right
        // destination then. Without this guard, any URL write from
        // a Copilot-rail-hosting page (Workbench, Requirements) would
        // navigate the user out of that page mid-conversation.
        if (
          !overridden &&
          (shellFromUrl === "workbench" || shellFromUrl === "requirements")
        ) {
          const to =
            shellFromUrl === "requirements"
              ? "/projects/$projectId/requirements"
              : "/projects/$projectId/workbench";
          void navigate({
            to,
            params: { projectId: targetProjectId },
            search: (prev: Record<string, unknown>) => ({
              ...prev,
              ...search,
            }),
            replace: true,
          });
          return;
        }
        void navigate({
          to: "/projects/$projectId/chat",
          params: { projectId: targetProjectId },
          search,
          replace: true,
        });
      } else {
        void navigate({ to: "/chat", search, replace: true });
      }
    },
    [navigate, projectIdFromUrl, shellFromUrl],
  );
}
